<template>
  <div class="lp-progress-wrapper">
    <div
      class="lp-progress-bar"
      :class="[
        infinite && 'lp-progress-bar-infinite',
        isNegative && 'bg-error',
      ]"
      :style="{ height: `${height}px` }"
    >
      <div
        v-if="!isNegative && !data"
        class="lp-progress-line"
        :style="!infinite && { width: `${progress}%` }"
      >
        <span
          v-if="showTooltip && !infinite"
          class="lp-progress-tooltip"
          :class="[`lp-progress-tooltip-${tooltipPlacement}`]"
          v-html="`${progress}%`"
        />
      </div>
      <div
        v-else
        class="lp-progress-bar-multiple"
        :style="{ height: `${height}px` }"
      >
        <lp-tooltip
          class="w-100"
          :content="tooltipContent"
          placement="top"
          :disabled="!showTooltip"
        >
          <div
            v-for="bar in data"
            :key="bar.label"
            class="lp-progress-line"
            :class="bar.color && `bg-${bar.color}`"
            :style="{ width: `${(bar.value * 100) / totalValue}%` }"
          />
        </lp-tooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Positions } from '@/models/enums'
import LpTooltip from '@/components/tooltip/LpTooltip.vue'
import { formatCurrency } from '@/helpers/numbers'
import { colorType } from '../../../dist/models/types'

defineOptions({
  name: 'lp-progressbar',
})
type ProgressBarDataSet = {
  label: string
  color: colorType
  value: number
}

type ProgressBarProps = {
  infinite?: boolean
  data?: ProgressBarDataSet[] | null
  progress?: number
  totalValue?: number
  totalLabel?: string
  totalColor?: string
  height?: number
  tooltipPlacement?: Positions
  showTooltip?: boolean
  isNegative?: boolean
  isCurrency?: boolean
}
const props = withDefaults(defineProps<ProgressBarProps>(), {
  data: null,
  progress: 0,
  height: 7,
  tooltipPlacement: Positions.T,
  showTooltip: true,
  isCurrency: true,
  totalColor: 'primary-100',
  totalValue: 0,
  totalLabel: '',
})

const totalValue = computed(() => {
  if (props.totalValue) return props.totalValue
  if (props.data) {
    return props.data.reduce(
      (accumulator, currentValue) => accumulator + currentValue.value,
      0,
    )
  }
  return 0
})

const barContent = (bar: ProgressBarDataSet) =>
  `<span class="h2 pr-half text-${
    bar.color
  }" style="line-height: 1; vertical-align: middle;">• </span> ${bar.label}: ${
    props.isCurrency ? formatCurrency(bar.value) : bar.value
  }</br>`

const tooltipContent = computed(() => {
  const totalContent = {
    value: totalValue.value,
    label: props.totalLabel,
    color: props.totalColor,
  }
  if (props.data)
    return props.data.reduce(
      (accumulator, bar) => {
        return accumulator + barContent(bar)
      },
      props.totalValue && props.totalLabel ? barContent(totalContent) : '',
    )
  return ''
})
</script>
